import { Box, Stack, Typography } from '@mui/material';
import {
	type CreateJobResponse,
	isClosedJobStatus,
	type JobResponse,
	type ReportCatalogEntry,
	type ReportCatalogKey,
	type ReportType,
	reportGroups,
} from '@parkchirp/reporting';
import { useEffect, useMemo, useState } from 'react';
import { ReportingLayout } from './ReportingLayout';
import { ReportStatusPanel } from './ReportStatusPanel';
import { reportComponents } from './report-components';
import { useCreateReport } from './useCreateReport';
import { useReportSse } from './useReportSse';

type Props = {
	authToken: string;
	baseUrl: string;
	organizationId?: number | null;
	onError: (message: string) => void;
	onValidationError: (message: string) => void;
};

const getReportFromCatalog = (key: ReportCatalogKey | null | undefined): ReportCatalogEntry | null => {
	if (!key) {
		return null;
	}

	for (const { reports } of reportGroups) {
		const report = reports.find((r: ReportCatalogEntry) => r.key === key);

		if (report) {
			return report;
		}
	}

	return null;
};

const downloadFile = (url: string, fileName: string) => {
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	link.click();
};

const jobIsRunning = (createdJob: CreateJobResponse | null, job: JobResponse | null) => {
	if (!createdJob) {
		return false;
	}

	if (job && job.jobId === createdJob.jobId) {
		return !isClosedJobStatus(job.status);
	}

	return !isClosedJobStatus(createdJob.status);
};

export const ReportingPage = ({ authToken, baseUrl, organizationId, onError, onValidationError }: Props) => {
	const [selectedReportKey, setSelectedReportKey] = useState<ReportType | null>(null);
	const [isStatusDismissed, setIsStatusDismissed] = useState(false);

	const { createReport, createdJob, error: createError, isCreatingReport } = useCreateReport({ authToken, baseUrl });

	const { job, error: sseError } = useReportSse({
		authToken,
		baseUrl,
		jobId: createdJob?.jobId,
	});

	const selectedReport = useMemo(() => {
		return getReportFromCatalog(selectedReportKey);
	}, [selectedReportKey]);

	const jobReport = getReportFromCatalog(job?.reportType);

	const ReportForm = useMemo(() => {
		if (!selectedReportKey) {
			return null;
		}

		return reportComponents[selectedReportKey];
	}, [selectedReportKey]);

	const isGeneratingReport = useMemo(() => {
		return isCreatingReport || jobIsRunning(createdJob, job);
	}, [createdJob, isCreatingReport, job]);

	useEffect(() => {
		if (createdJob) {
			setIsStatusDismissed(false);
		}
	}, [createdJob]);

	useEffect(() => {
		const error = createError ?? sseError;

		if (error) {
			onError(error);
		}
	}, [createError, onError, sseError]);

	const showStatusPanel = useMemo(() => {
		if (!job) {
			return false;
		}

		if (!jobReport) {
			return false;
		}

		if (isStatusDismissed) {
			return false;
		}

		if (job.jobId !== createdJob?.jobId) {
			return false;
		}

		return true;
	}, [job, createdJob, jobReport, isStatusDismissed]);

	return (
		<ReportingLayout
			selectedReportKey={selectedReportKey}
			navigationDisabled={isGeneratingReport}
			onSelectReport={setSelectedReportKey}
		>
			{selectedReport && ReportForm ? (
				<Stack spacing={2.5}>
					<Box
						sx={{
							borderBottom: 1,
							borderColor: 'divider',
							pb: 1.5,
						}}
					>
						<Typography variant='h5' fontWeight={700}>
							{selectedReport.title}
						</Typography>
					</Box>

					<ReportForm
						createReport={createReport}
						isGeneratingReport={isGeneratingReport}
						organizationId={organizationId}
						onValidationError={onValidationError}
					/>
				</Stack>
			) : (
				<Box
					sx={{
						alignItems: 'center',
						display: 'flex',
						justifyContent: 'center',
						minHeight: 320,
					}}
				>
					<Typography color='text.secondary'>Select a report to configure.</Typography>
				</Box>
			)}

			{showStatusPanel && (
				<ReportStatusPanel
					job={job}
					reportTitle={jobReport.title}
					onDismiss={() => setIsStatusDismissed(true)}
					onDownload={downloadFile}
				/>
			)}
		</ReportingLayout>
	);
};
